import { ACTION_TYPES } from "./actionTypes";
import { initialStore } from "./initialStore";
import { safeArray, safeJsonParse, safeNumber } from "../utils/validations";

const applyDefaultFuelZeroing = (portOps, queryData) => {
  const fuelCalcMethod = queryData.fuelCalculationMethod || "DEFAULT";
  if (fuelCalcMethod === "DEFAULT") {
    return portOps.map((item) => ({
      ...item,
      cargoDetails: {
        ...(item.cargoDetails || {}),
        fuelPriceType0Hsfo: 0,
        fuelPriceType0Lsfo: 0,
        fuelPriceType0Mgo: 0,
        fuelPriceType1Hsfo: 0,
        fuelPriceType1Lsfo: 0,
        fuelPriceType1Mgo: 0,
        fuelPriceType2Hsfo: 0,
        fuelPriceType2Lsfo: 0,
        fuelPriceType2Mgo: 0,
        fuelPriceType3Hsfo: 0,
        fuelPriceType3Lsfo: 0,
        fuelPriceType3Mgo: 0,
        fuelPriceType4Hsfo: 0,
        fuelPriceType4Lsfo: 0,
        fuelPriceType4Mgo: 0,
      },
    }));
  }
  return portOps;
};

export function calculatorReducer(state, action) {
  switch (action.type) {
    case ACTION_TYPES.SET_INITIAL_DATA: {
      const { allQueries, fleetList, selectedQuery, pagination } =
        action.payload;
      const safeQueries = safeArray(allQueries);
      const safeFleets = safeArray(fleetList);
      const activeQuery = selectedQuery || safeQueries[0] || {};

      if (!activeQuery.fuelCalculationMethod)
        activeQuery.fuelCalculationMethod = "DEFAULT";
      if (!activeQuery.fuelType) activeQuery.fuelType = "TYPE0";

      const activeFleet = safeFleets[0] || {};
      const cargoList = safeArray(activeQuery.cargoList);
      let portOps = cargoList.map((item, idx) => ({ ...item, index: idx }));

      portOps = applyDefaultFuelZeroing(portOps, activeQuery);

      if (portOps.length > 0 && activeFleet.port) {
        const vesselPort = activeFleet.port || "";
        const vesselObj = safeJsonParse(activeFleet.vessel, {});
        const currentType = (
          vesselObj.fuelType ||
          activeQuery.fuelType ||
          "TYPE0"
        ).replace("TYPE", "");
        const {
          bnkDelhs = 0,
          bnkDells = 0,
          bnkDelmgo = 0,
          bnkPriceDeliveryhs = 0,
          bnkPriceDeliveryls = 0,
          bnkPriceDeliverymgo = 0,
        } = activeFleet;

        const firstPort = { ...portOps[0] };
        firstPort.port = vesselPort;
        firstPort.cargoDetails = {
          ...(firstPort.cargoDetails || {}),
          bunkerSuppliedHSFO: bnkDelhs,
          bunkerSuppliedLSFO: bnkDells,
          bunkerSuppliedMGO: bnkDelmgo,
          [`fuelPriceType${currentType}Hsfo`]: bnkPriceDeliveryhs,
          [`fuelPriceType${currentType}Lsfo`]: bnkPriceDeliveryls,
          [`fuelPriceType${currentType}Mgo`]: bnkPriceDeliverymgo,
        };
        portOps[0] = firstPort;
      }

      return {
        ...state,
        allQueryFormat: safeQueries,
        fleetList: safeFleets,
        selectedQuery: activeQuery,
        portOperation: portOps,
        selectedVessel: activeFleet,
        pagination: { ...state.pagination, ...pagination },
        error: null,
      };
    }

    case ACTION_TYPES.SET_ALL_QUERIES:
      return {
        ...state,
        allQueryFormat: safeArray(action.payload),
      };

    case ACTION_TYPES.SET_SELECTED_QUERY: {
      const query = action.payload || {};

      if (!query.fuelCalculationMethod) query.fuelCalculationMethod = "DEFAULT";
      if (!query.fuelType) query.fuelType = "TYPE0";

      const cargoList = safeArray(query.cargoList);
      let portOps = cargoList.map((item, idx) => ({ ...item, index: idx }));

      portOps = applyDefaultFuelZeroing(portOps, query);

      const activeFleet = state.selectedVessel || state.fleetList?.[0] || {};
      if (portOps.length > 0 && activeFleet.port) {
        const vesselPort = activeFleet.port || "";
        const vesselObj = safeJsonParse(activeFleet.vessel, {});
        const currentType = (
          vesselObj.fuelType ||
          query.fuelType ||
          "TYPE0"
        ).replace("TYPE", "");
        const {
          bnkDelhs = 0,
          bnkDells = 0,
          bnkDelmgo = 0,
          bnkPriceDeliveryhs = 0,
          bnkPriceDeliveryls = 0,
          bnkPriceDeliverymgo = 0,
        } = activeFleet;

        const firstPort = { ...portOps[0] };
        firstPort.port = vesselPort;
        firstPort.cargoDetails = {
          ...(firstPort.cargoDetails || {}),
          bunkerSuppliedHSFO: bnkDelhs,
          bunkerSuppliedLSFO: bnkDells,
          bunkerSuppliedMGO: bnkDelmgo,
          [`fuelPriceType${currentType}Hsfo`]: bnkPriceDeliveryhs,
          [`fuelPriceType${currentType}Lsfo`]: bnkPriceDeliveryls,
          [`fuelPriceType${currentType}Mgo`]: bnkPriceDeliverymgo,
        };
        portOps[0] = firstPort;
      }

      return {
        ...state,
        selectedQuery: query,
        portOperation: portOps,
      };
    }

    case ACTION_TYPES.SET_PORT_OPERATIONS:
      return {
        ...state,
        portOperation: safeArray(action.payload).map((item, idx) => ({
          ...item,
          index: idx,
        })),
      };

    case ACTION_TYPES.UPDATE_PORT_OPERATION: {
      const { index, name, value, cargoUpdate } = action.payload;
      const currentOps = safeArray(state.portOperation);
      if (index < 0 || index >= currentOps.length) return state;

      const updatedOps = [...currentOps];
      const targetOp = { ...updatedOps[index] };

      if (cargoUpdate) {
        targetOp.cargoDetails = {
          ...(targetOp.cargoDetails || {}),
          [name]: value,
        };
      } else {
        targetOp[name] = value;
      }

      updatedOps[index] = targetOp;

      return {
        ...state,
        portOperation: updatedOps,
      };
    }

    case ACTION_TYPES.REORDER_PORT_OPERATIONS: {
      const { oldIndex, newIndex } = action.payload;
      const currentOps = safeArray(state.portOperation);
      if (
        oldIndex < 0 ||
        oldIndex >= currentOps.length ||
        newIndex < 0 ||
        newIndex >= currentOps.length ||
        oldIndex === newIndex
      ) {
        return state;
      }

      const reorderedOps = [...currentOps];
      const [movedItem] = reorderedOps.splice(oldIndex, 1);
      reorderedOps.splice(newIndex, 0, movedItem);

      const reIndexedOps = reorderedOps.map((item, idx) => ({
        ...item,
        index: idx,
      }));

      return {
        ...state,
        portOperation: reIndexedOps,
      };
    }

    case ACTION_TYPES.ADD_PORT_OPERATION: {
      const newPort = action.payload;
      const currentOps = safeArray(state.portOperation);
      const updatedOps = [
        ...currentOps,
        { ...newPort, index: currentOps.length },
      ];

      return {
        ...state,
        portOperation: updatedOps,
      };
    }

    case ACTION_TYPES.DELETE_PORT_OPERATION: {
      const targetIndex = action.payload;
      const currentOps = safeArray(state.portOperation);
      if (targetIndex < 0 || targetIndex >= currentOps.length) return state;

      const filteredOps = currentOps.filter((_, idx) => idx !== targetIndex);
      const reIndexedOps = filteredOps.map((item, idx) => ({
        ...item,
        index: idx,
      }));

      return {
        ...state,
        portOperation: reIndexedOps,
      };
    }

    case ACTION_TYPES.SET_FLEET_LIST:
      return {
        ...state,
        fleetList: safeArray(action.payload),
      };

    case ACTION_TYPES.SET_SELECTED_VESSEL:
      return {
        ...state,
        selectedVessel: action.payload || {},
      };

    case ACTION_TYPES.SET_PORT_DISTANCES:
      return {
        ...state,
        portDistances: {
          output: action.payload.output || {},
          payload: action.payload.payload || {},
        },
      };

    case ACTION_TYPES.SET_CALCULATION_RESULT:
      return {
        ...state,
        calculationResult: action.payload || {},
      };

    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        loader: {
          loading: Boolean(action.payload?.loading),
          message: action.payload?.message || "",
        },
      };

    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };

    case ACTION_TYPES.SET_IS_OVERRIDE:
      return {
        ...state,
        isOverride: Boolean(action.payload),
      };

    case ACTION_TYPES.RESET_STATE:
      return { ...initialStore };

    default:
      return state;
  }
}
